import { BaseDateRangeType } from 'application-types';
import { doDateRangesOverlap } from 'async-timing';

import { ReservableAvailabilityReason } from './types';

// Surface remaining spots to the user once capacity drops to this many.
export const LOW_CAPACITY_THRESHOLD = 10;
// Below this, remaining capacity is treated as critical rather than merely low.
export const CRITICAL_CAPACITY_THRESHOLD = 3;

export enum SlotCapacityStatus {
  Available = 'Available',
  Low = 'Low',
  Critical = 'Critical',
  Full = 'Full',
  UserCapReached = 'UserCapReached',
}

// The client-side view of a capacity interval, as returned by the
// contentFeatureReservableRemainingCapacity query.
export type SlotCapacityRange = {
  interval: BaseDateRangeType;
  remainingSpots: number | null;
  reason: ReservableAvailabilityReason | null;
};

export type SlotCapacity = {
  status: SlotCapacityStatus;
  remainingSpots: number | null;
  isBookable: boolean;
};

type ResolvedCapacity = {
  reason: ReservableAvailabilityReason | null;
  remainingSpots: number | null;
};

// Every capacity interval a time range touches. A range longer than one
// interval - as custom time slots can be - overlaps more than one.
export function findOverlappingCapacity(
  range: BaseDateRangeType,
  availability: SlotCapacityRange[]
): SlotCapacityRange[] {
  return availability.filter(entry =>
    doDateRangesOverlap(range, entry.interval)
  );
}

// Collapses every capacity interval a time range covers into one verdict. A
// range is only bookable if every interval it covers is bookable, so a blocking
// reason on any overlap wins and remaining spots is the minimum.
export function resolveSlotCapacity(
  matches: SlotCapacityRange[],
  maxQuantityPerSlot?: number
): ResolvedCapacity {
  if (matches.length === 0) {
    // No capacity interval means nothing is booked against this range yet.
    return { reason: null, remainingSpots: maxQuantityPerSlot ?? null };
  }

  const isFull = matches.some(
    match =>
      match.reason === ReservableAvailabilityReason.Full ||
      match.remainingSpots === 0
  );
  const isUserCapReached = matches.some(
    match => match.reason === ReservableAvailabilityReason.UserCapReached
  );

  let reason: ReservableAvailabilityReason | null = null;

  if (isFull) {
    reason = ReservableAvailabilityReason.Full;
  } else if (isUserCapReached) {
    reason = ReservableAvailabilityReason.UserCapReached;
  }

  const knownRemainingSpots = matches
    .map(match => match.remainingSpots)
    .filter(
      (remainingSpots): remainingSpots is number => remainingSpots !== null
    );

  return {
    reason,
    remainingSpots: knownRemainingSpots.length
      ? Math.min(...knownRemainingSpots)
      : null,
  };
}

export function getSlotCapacityStatus(
  reason: ReservableAvailabilityReason | null,
  remainingSpots: number | null
): SlotCapacity {
  if (reason === ReservableAvailabilityReason.Full || remainingSpots === 0) {
    return {
      status: SlotCapacityStatus.Full,
      remainingSpots,
      isBookable: false,
    };
  }

  if (reason === ReservableAvailabilityReason.UserCapReached) {
    return {
      status: SlotCapacityStatus.UserCapReached,
      remainingSpots,
      isBookable: false,
    };
  }

  if (
    remainingSpots != null &&
    remainingSpots > 0 &&
    remainingSpots <= LOW_CAPACITY_THRESHOLD
  ) {
    return {
      status:
        remainingSpots <= CRITICAL_CAPACITY_THRESHOLD
          ? SlotCapacityStatus.Critical
          : SlotCapacityStatus.Low,
      remainingSpots,
      isBookable: true,
    };
  }

  return {
    status: SlotCapacityStatus.Available,
    remainingSpots,
    isBookable: true,
  };
}

// The capacity verdict for a single bookable time range. Callers gate on
// maxQuantityPerSlot being set, which only happens when the headcount-aware
// capacity flag is on.
export function getSlotCapacityForRange(
  range: BaseDateRangeType,
  availability: SlotCapacityRange[],
  maxQuantityPerSlot?: number
): SlotCapacity {
  const { reason, remainingSpots } = resolveSlotCapacity(
    findOverlappingCapacity(range, availability),
    maxQuantityPerSlot
  );

  return getSlotCapacityStatus(reason, remainingSpots);
}
